import type { CandidateTimelineRound } from "@shared/labs/level-assessment-candidate";
import type { EvidenceClaim } from "@shared/labs/level-assessment-round-update";

/**
 * Pure derivations for the candidate case-file lenses.
 *
 * Convergência: the estimate trajectory — one point per round, carrying the
 * last scored estimate through rounds that didn't re-score, so the chart shows
 * the measurement's state after EVERY round (hollow markers = carried).
 *
 * Evidências: the claims explorer grid — every quote-verified claim from every
 * fusion round, pivoted axis × round. The catalogue as a surface.
 */

// ── Convergência ─────────────────────────────────────────────────────────────

export interface ConvergencePoint {
  roundId: string;
  sequence: number;
  label: string;
  mean: number | null;
  stddev: number | null;
  level: number | null;
  confidence: number | null;
  /** False when this round carried the previous estimate forward. */
  scored: boolean;
}

/** Rounds → trajectory points. Rounds still evaluating (no estimate yet and
 *  nothing to carry) are skipped; a carried round repeats the last scored
 *  estimate with `scored: false`. */
export function convergenceSeries(rounds: CandidateTimelineRound[]): ConvergencePoint[] {
  const out: ConvergencePoint[] = [];
  let last: Omit<ConvergencePoint, "roundId" | "sequence" | "label" | "scored"> | null = null;
  for (const r of rounds) {
    const ev = r.currentEvaluation;
    if (ev) {
      last = {
        mean: ev.overallMean,
        stddev: ev.overallStddev,
        level: ev.overallLevel,
        confidence: ev.overallConfidence,
      };
      out.push({
        roundId: r.id,
        sequence: r.sequence,
        label: r.label ?? `Rodada ${r.sequence}`,
        ...last,
        scored: true,
      });
    } else if (last) {
      out.push({
        roundId: r.id,
        sequence: r.sequence,
        label: r.label ?? `Rodada ${r.sequence}`,
        ...last,
        scored: false,
      });
    }
    // No evaluation and nothing to carry (round 1 still evaluating) → skip.
  }
  return out;
}

// ── Evidências ───────────────────────────────────────────────────────────────

/** Mirrors the assembler's synthetic axis for claims routed to no axis. */
export const PROFILE_ROW_ID = "profile-geral";
export const PROFILE_ROW_LABEL = "Perfil geral";

export interface EvidenceColumn {
  roundId: string;
  sequence: number;
  label: string;
}

export interface EvidenceRow {
  axisId: string;
  axisLabel: string;
  scope: "cross" | "vertical" | "profile";
  /** Claims per column, aligned with `columns` by index. */
  cells: EvidenceClaim[][];
  total: number;
}

export interface EvidenceGrid {
  columns: EvidenceColumn[];
  rows: EvidenceRow[];
  totalClaims: number;
}

export interface AxisLabelEntry {
  id: string;
  label: string;
  scope: "cross" | "vertical";
}

/** Flatten a loaded model into the ordered axis list (cross first, then each
 *  discipline's verticals) the grid uses for row order and labels. */
export function axisLabelEntries(model: {
  crossAxes: { id: string; label: string }[];
  disciplines: { id: string; axes: { id: string; label: string }[] }[];
}): AxisLabelEntry[] {
  const out: AxisLabelEntry[] = model.crossAxes.map((a) => ({
    id: a.id,
    label: a.label,
    scope: "cross" as const,
  }));
  for (const d of model.disciplines) {
    for (const a of d.axes) out.push({ id: a.id, label: a.label, scope: "vertical" });
  }
  return out;
}

/** Pivot per-round claims into the axis × round grid. A claim routed to N axes
 *  appears in each (the same observation genuinely informed each competency);
 *  a claim routed to none lands on the "Perfil geral" row. Rows keep model
 *  order; axes with no claims are omitted (the grid shows evidence, not the
 *  rubric). Claims for an axis the model no longer names are grouped under the
 *  profile row rather than dropped. */
export function buildEvidenceGrid(
  perRound: { round: EvidenceColumn; claims: EvidenceClaim[] }[],
  axes: AxisLabelEntry[],
): EvidenceGrid {
  const columns = perRound.map((p) => p.round);
  const byAxis = new Map<string, EvidenceClaim[][]>();
  const ensure = (axisId: string): EvidenceClaim[][] => {
    let cells = byAxis.get(axisId);
    if (!cells) {
      cells = columns.map(() => []);
      byAxis.set(axisId, cells);
    }
    return cells;
  };

  const known = new Set(axes.map((a) => a.id));
  let totalClaims = 0;
  perRound.forEach(({ claims }, col) => {
    for (const claim of claims) {
      totalClaims++;
      const targets = claim.axes.filter((a) => known.has(a.axisId)).map((a) => a.axisId);
      if (targets.length === 0) {
        ensure(PROFILE_ROW_ID)[col].push(claim);
      } else {
        for (const axisId of targets) ensure(axisId)[col].push(claim);
      }
    }
  });

  const rows: EvidenceRow[] = [];
  for (const axis of axes) {
    const cells = byAxis.get(axis.id);
    if (!cells) continue;
    const total = cells.reduce((n, c) => n + c.length, 0);
    if (total === 0) continue;
    rows.push({ axisId: axis.id, axisLabel: axis.label, scope: axis.scope, cells, total });
  }
  const profileCells = byAxis.get(PROFILE_ROW_ID);
  if (profileCells) {
    const total = profileCells.reduce((n, c) => n + c.length, 0);
    if (total > 0) {
      rows.push({
        axisId: PROFILE_ROW_ID,
        axisLabel: PROFILE_ROW_LABEL,
        scope: "profile",
        cells: profileCells,
        total,
      });
    }
  }
  return { columns, rows, totalClaims };
}

/** Client-facing word for a claim's speaker. */
export const SPEAKER_LABEL: Record<EvidenceClaim["speaker"], string> = {
  candidate: "candidato",
  interviewer: "entrevistador",
  document: "documento",
  unclear: "fonte incerta",
};
